// A photo display that opens a full-screen pan/zoom inspector on click.
// Closes on background click or escape. Pinch on mobile, scroll/drag on desktop.

import { useEffect, useState } from 'react'
import { TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch'
import { useT } from '../i18n'

type Props = {
  src: string
  alt?: string
  className?: string
}

export default function PhotoViewer({ src, alt = '', className = '' }: Props) {
  const { t, locale } = useT()
  const [open, setOpen] = useState(false)

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('keydown', onKey)
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = ''
    }
  }, [open])

  return (
    <>
      <div className={`relative bg-earth-900 rounded-lg overflow-hidden group ${className}`}>
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label={t('play.inspect')}
          className="block w-full h-full focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-300"
        >
          <img
            src={src}
            alt={alt}
            className="w-full h-full max-h-[44vh] sm:max-h-[640px] object-contain mx-auto"
            loading="eager"
          />
        </button>
        <span
          aria-hidden
          className="pointer-events-none absolute top-2 end-2 bg-black/65 text-white/95 text-[11px] sm:text-xs rounded-md px-2.5 py-1.5 backdrop-blur-sm flex items-center gap-1.5 transition-opacity opacity-80 group-hover:opacity-100"
          dir="ltr"
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
            <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />
          </svg>
          <span>{t('play.inspect')}</span>
        </span>
      </div>

      {open && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={t('play.inspect')}
          onClick={() => setOpen(false)}
          className="fixed inset-0 z-50 eg-viewer-bg eg-fade-in flex flex-col"
        >
          <header className="flex items-center justify-between p-3 sm:p-5 text-white/80">
            <span className="text-xs sm:text-sm uppercase tracking-[0.2em]">
              {t('play.inspect')}
            </span>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label={t('play.close')}
              className="w-9 h-9 sm:w-10 sm:h-10 rounded-full hover:bg-white/10 flex items-center justify-center text-xl"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
          </header>

          <div className="flex-1 overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <TransformWrapper
              minScale={0.5}
              maxScale={8}
              initialScale={1}
              centerOnInit
              wheel={{ step: 0.1 }}
              doubleClick={{ mode: 'reset' }}
            >
              <TransformComponent
                wrapperStyle={{ width: '100%', height: '100%' }}
                contentStyle={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                <img src={src} alt={alt} className="max-w-none select-none" draggable={false} />
              </TransformComponent>
            </TransformWrapper>
          </div>

          <footer
            className="p-3 sm:p-4 text-center text-[11px] sm:text-xs text-white/55"
            dir={locale === 'ar' ? 'rtl' : 'ltr'}
          >
            {t('play.viewer_hint')}
          </footer>
        </div>
      )}
    </>
  )
}
